import { useMutation, useMutationState } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { getProvider } from '@galapagos/observability';
import { ONBOARDING_INTAKE_FLOW_ID } from '@galapagos/shared';
import { useSignupApi } from '../hooks/useSignupApi';
import { useSignupProgramOptions } from '../hooks/useSignupProgramOptions';
import { selectedProgramKeys } from '../services/program-options';
import { trackRegFlowQuizCompleted } from '../services/regflow-analytics';
import { selectedSubOptionAnswers } from '../services/sub-option-answers';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import { backfillBaselineCheckIns } from './baseline-backfill';
import {
  finalizeRetryDelay,
  isAlreadyFinalized,
  isTransientFinalizeFailure,
  shouldRetryFinalize,
} from './finalize-retry';

export type OnboardingFinalizeState =
  | { status: 'pending' }
  | { status: 'done' }
  | { status: 'failed'; retry: () => void };

type FinalizeSkipReason = 'no_program_key' | 'options_unavailable';

/** The flow's finalize writes, keyed to the flow so an earlier flow's never counts for this one. */
function finalizeKey(flowEpoch: number) {
  return ['signup', 'onboarding-finalize', flowEpoch] as const;
}

const isCurrentFlow = (flowEpoch: number) => useSignupFlowStore.getState().flowEpoch === flowEpoch;

/**
 * One finalize write: the program keys, the sub-type answers chosen with them,
 * and the flow that asked for them.
 */
const finalizeRequestSchema = z.object({
  programKeys: z.array(z.string()),
  subOptionAnswers: z.record(z.string(), z.string()),
  flowEpoch: z.number(),
});

type FinalizeRequest = z.infer<typeof finalizeRequestSchema>;

/** A write whose flow has moved on; never sent and never retried. */
class StaleFlowError extends Error {
  constructor() {
    super('The signup flow this finalize belongs to has ended');
    this.name = 'StaleFlowError';
  }
}

/**
 * Finalize the member's intake session as wrap-up opens, once per flow: the
 * program keys from the substance step go in the same write, so the baseline
 * the server writes inside the finalize sees them, and the program the
 * finalize enrolls receives the reported week as check-ins. The sub-type
 * answer of each program that has one goes with them, so that program is
 * enrolled with its sub-type.
 *
 * A server-gated, blocking write (never optimistic). A failure that can clear
 * on its own is retried a few times; after that, or on any other failure, the
 * caller shows an error with Try again.
 *
 * The write lives in the shared mutation cache under the flow's key, not in
 * the step: returning to wrap-up while it is in flight waits on that same
 * write instead of sending another, and a write that settles after the flow
 * was reset or the member signed out changes nothing for the next flow.
 */
export function useOnboardingFinalize(): OnboardingFinalizeState {
  const api = useSignupApi();
  const options = useSignupProgramOptions();
  const selection = useSignupFlowStore((state) => state.programSelection);
  const flowEpoch = useSignupFlowStore((state) => state.flowEpoch);
  const mutationKey = finalizeKey(flowEpoch);
  const [skippedEpoch, setSkippedEpoch] = useState<number | null>(null);
  const startedEpoch = useRef<number | null>(null);

  const finalize = useMutation({
    mutationKey,
    // Checked before every attempt, each retry included: a flow that ended
    // while a retry waited out its delay no longer wants the write, and it
    // would go out under whoever is signed in now. The flow comes from the
    // request, which never changes, not from this render.
    mutationFn: ({ programKeys, subOptionAnswers, flowEpoch: requestEpoch }: FinalizeRequest) => {
      if (!isCurrentFlow(requestEpoch)) return Promise.reject(new StaleFlowError());
      return api.answerOnboarding(ONBOARDING_INTAKE_FLOW_ID, {
        answers: { ...subOptionAnswers, program_key: programKeys },
        finalize: true,
      });
    },
    retry: (failureCount, error) => {
      if (error instanceof StaleFlowError) return false;
      if (shouldRetryFinalize(failureCount, error)) return true;
      if (!isAlreadyFinalized(error)) {
        getProvider().logs.log('warn', 'galapagos.signup.onboarding_finalize_failed', {
          reason: isTransientFinalizeFailure(error) ? 'transient' : 'permanent',
        });
      }
      return false;
    },
    retryDelay: finalizeRetryDelay,
    // A failure must reach the member even while the browser reports itself
    // offline; the default mode would pause the write and leave them waiting.
    networkMode: 'always',
    // Kept for the whole visit, so coming back to wrap-up later in the same
    // flow still finds this flow's finalize and does not send another.
    gcTime: Infinity,
    onSuccess: (session, request) => {
      // Only the atomic winner counts completion, even if the flow has moved on.
      if (session.firstCompletion !== false) trackRegFlowQuizCompleted();
      // A replay may be recovering a lost first response. The existing per-date
      // correcting append lets daily tracking recover without duplicate days.
      if (isCurrentFlow(request.flowEpoch)) backfillBaselineCheckIns(api, session);
    },
  });
  const { mutate } = finalize;

  const latest = useMutationState({
    filters: { mutationKey, exact: true },
    select: (mutation) => ({
      status: mutation.state.status,
      error: mutation.state.error,
      variables: mutation.state.variables,
    }),
  }).at(-1);
  const alreadyStarted = latest !== undefined;

  useEffect(() => {
    if (alreadyStarted || startedEpoch.current === flowEpoch || options.isPending) return;
    startedEpoch.current = flowEpoch;
    const served = options.data?.options ?? [];
    const keys = selectedProgramKeys(served, selection);
    if (keys.length === 0) {
      // Only a reload (which empties the in-memory pick) or an options read
      // that failed gets here. A session completed with no program would
      // leave the member with no baseline and no enrollment for good; left
      // open, the app's own intake finishes it.
      const reason: FinalizeSkipReason =
        options.isError && selection?.length ? 'options_unavailable' : 'no_program_key';
      getProvider().logs.log('warn', 'galapagos.signup.onboarding_finalize_skipped', { reason });
      setSkippedEpoch(flowEpoch);
      return;
    }
    mutate({
      programKeys: keys,
      subOptionAnswers: selectedSubOptionAnswers(served, selection),
      flowEpoch,
    });
  }, [
    alreadyStarted,
    flowEpoch,
    options.isPending,
    options.isError,
    options.data,
    selection,
    mutate,
  ]);

  if (skippedEpoch === flowEpoch) return { status: 'done' };
  if (latest?.status === 'success') return { status: 'done' };
  if (latest?.status === 'error') {
    // An earlier finalize committed without this client seeing its answer:
    // the session is complete, but its program id is not in hand, so there is
    // no backfill to run.
    if (isAlreadyFinalized(latest.error)) return { status: 'done' };
    const parsed = finalizeRequestSchema.safeParse(latest.variables);
    const request = parsed.success
      ? parsed.data
      : { programKeys: [], subOptionAnswers: {}, flowEpoch };
    // Try again starts a fresh write under the same retry policy.
    return { status: 'failed', retry: () => mutate(request) };
  }
  return { status: 'pending' };
}
